<template>
  <v-btn
    prepend-icon="fas fa-times"
    color="error"
    block
    size="default"
    class="mb-1"
    @click="show"
  >
    {{ t('shift.cancellation.title') }}
    <v-dialog
      v-model="cancellationDialog"
      max-width="500px"
    >
      <global-card
        heading-color="error"
        closable
        :title="t('shift.cancellation.title')"
        @close="cancellationDialog = false"
      >
        <v-select
          v-model="selectedReasonId"
          :label="t('cancellation_reason.title')"
          item-value="id"
          item-title="reason"
          :items="cancellationReasons"
        />
        <div
          v-if="user.id === authUser.id"
          class="text-body-large"
        >
          <i18n-t
            keypath="shift.cancellation.confirmation_message_you"
          >
            <template #day>
              <span class="font-weight-semibold">
                {{ toLongDateWithDay(shift.date) }}
              </span>
            </template>
            <template #location>
              <span class="font-weight-semibold">
                {{ shift.location?.name }}
              </span>
            </template>
          </i18n-t>
        </div>
        <div
          v-else
          class="text-body-large"
        >
          <i18n-t
            keypath="shift.cancellation.confirmation_message_user"
          >
            <template #user>
              <span class="font-weight-semibold">{{ user.full_name }}</span>
            </template>
            <template #day>
              <span class="font-weight-semibold">{{ toLongDateWithDay(shift.date) }}</span>
            </template>
            <template #location>
              <span class="font-weight-semibold">{{ shift.location?.name }}</span>
            </template>
          </i18n-t>
          <br>
          {{ t('shift.cancellation.confirmation_message_user_cancel_rate') }}
        </div>
        <br>
        <div
          v-if="user.id === authUser.id"
          class="text-body-large"
        >
          <span
            class="font-weight-semibold"
          >{{ t('shift.cancellation.current_info') }}</span>
          <br>
          <ul class="list-group ml-4 text-font">
            <li class="list-group-item text-body-large">
              {{ t('shift.cancellation.rate') }}: <span class="font-weight-semibold">{{ cancellationRate }}%</span>
            </li>
            <li class="list-group-item text-body-large">
              {{ t('shift.cancellation.last_minute_rate') }}: <span class="font-weight-semibold">{{ lastMinuteCancellationRate }}%</span>
            </li>
          </ul>
          <v-alert
            v-if="shift.dependent_publishers.length > 0"
            type="error"
            class="mt-2"
            :text="t('shift.cancel.warning_cancel_with_child_user')"
          >
            <ul class="mt-2">
              <li
                v-for="u in shift.dependent_publishers"
                :key="u.id"
                class="text-body-medium"
              >
                {{ u.full_name }}
              </li>
            </ul>
          </v-alert>
        </div>
        <template #actions>
          <v-btn
            :text="t('shift.dont_cancel')"
            color="dark-grey"
            @click="cancellationDialog = false"
          />
          <v-btn
            v-if="shift.spouse_assigned"
            :disabled="!selectedReasonId"
            :text="t('shift.cancellation.with_spouse')"
            color="error"
            @click="performCancellation(true)"
          />
          <v-btn
            :disabled="!selectedReasonId"
            :text="t('shift.cancellation.title')"
            color="error"
            @click="performCancellation(false)"
          />
        </template>
      </global-card>
    </v-dialog>
    <v-dialog
      v-model="lastMinCancellationDialog"
      max-width="600px"
    >
      <global-card
        heading-color="error"
        :title="t('shift.cancellation.last_minute')"
      >
        <div>
          <i18n-t
            keypath="shift.cancellation.confirmation_message"
          >
            <template #relative_date>
              {{ scheduledFor }}
            </template>
            <template #date>
              {{ toUniversalDate(shift.date) }}
            </template>
            <template #overseer>
              <a href="/dayoverseers">{{ t('dayoverseer.overseer') }}</a>
            </template>
            <template #weekday>
              <b>{{ weekdayName }}</b>
            </template>
          </i18n-t>
        </div>
        <div class="text-body-large">
          <div class="font-weight-semibold">
            {{ t('shift.cancellation.current_info') }}
          </div>
          <ul class="list-group">
            <li class="list-group-item">
              {{ t('shift.cancellation.rate') }}: <span class="font-weight-semibold">
                {{ cancellationRate }}%
              </span>
            </li>
            <li class="list-group-item">
              {{ t('shift.cancellation.last_minute_rate') }}: <span class="font-weight-bold text-font">
                {{ lastMinuteCancellationRate }}%
              </span>
            </li>
          </ul>
        </div>
        <template #actions>
          <v-btn
            :text="t('global.ok')"
            color="primary"
            @click="lastMinCancellationDialog = false"
          />
        </template>
      </global-card>
    </v-dialog>
  </v-btn>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useAuth,
  useDate,
  usePermissions,
} from '@/composables';
import { useErrorStore } from '@/stores/errors';

const props = defineProps<{
  user: App.Models.User;
  shift: App.ApiResources.PublisherWithShiftDetailsResource;
}>();

const emit = defineEmits(['shift:cancelled']);

const { t } = useI18n();

const { handleError } = useErrorStore();
const { authUser } = useAuth();
const { can } = usePermissions();
const {
  toUniversalDate,
  toLongDateWithDay,
  toLongDay,
  daysDiffFromToday,
} = useDate();

const cancellationReasons = ref<Array<App.Models.ShiftCancellationReason>>([]);
const selectedReasonId = ref<number>();
const lastMinCancellationDialog = ref(false);
const cancellationDialog = ref(false);

const canCancelLastMinute = computed<boolean>(() => can('manage_shifts'));
const isLastMinute = computed<boolean>(() => props.shift?.is_last_minute ?? false);
const cancellationRate = computed<string>(() => (Number.parseFloat(props.user.calc_cancellation_rate) * 100).toFixed(2));
const lastMinuteCancellationRate = computed<string>(() => (Number.parseFloat(props.user.calc_last_minute_cancellation_rate) * 100).toFixed(2));
const weekdayName = computed<string>(() => {
  if (props.shift.is_holiday === true) {
    return t('dow.0');
  }
  return toLongDay(props.shift.date);
});

const scheduledFor = computed(() => {
  const shiftDateDiff = daysDiffFromToday(props.shift.date);
  if (shiftDateDiff === 0) {
    return t('date.today');
  }
  if (shiftDateDiff === 1) {
    return t('date.tomorrow');
  }
  return weekdayName;
});

function show(): void {
  if (isLastMinute.value && !canCancelLastMinute.value) {
    lastMinCancellationDialog.value = true;
  }
  else {
    cancellationDialog.value = true;
  }
}

async function loadCancellationReasons(): Promise<void> {
  try {
    const response = await axios.get('/api/shiftcancellationreasons');
    cancellationReasons.value = response.data.data;
    if (cancellationReasons.value.length === 1) {
      selectedReasonId.value = cancellationReasons.value[0].id;
    }
  }
  catch (error) {
    handleError(error, t('cancellation_reason.cannot_load'));
  }
}

async function performCancellation(withSpouse = false): Promise<void> {
  try {
    await axios.patch(`/api/users/${props.user.id}/shifts/${props.shift.shift_id}?action=cancel`, {
      cancellation_reason_id: selectedReasonId.value,
    });
    if (withSpouse && props.user?.spouse_id) {
      await axios.patch(`/api/users/${props.user?.spouse_id}/shifts/${props.shift.shift_id}?action=cancel`, {
        cancellation_reason_id: selectedReasonId.value,
      });
    }
    emit('shift:cancelled');
    cancellationDialog.value = false;
  }
  catch (error) {
    handleError(error, t('shift.cancellation.error_message'));
  }
}

onMounted(() => {
  loadCancellationReasons();
});
</script>
